import axios from 'axios';
import { config as confFunc, ignoreLoginPath } from 'env_def';
import { checkMasterInfo } from '@mail/jmail';
import { api } from '@/api/api';
import { apis, environment, getPageName, isElectron, inWindow } from '@/config';
import { ErrMsgCodeMap } from '@/api/errMap';
import { constHttpCanceledToken, } from '@/api/data/http';
import { SequenceHelper } from '@/api/commonModel';
import { httpCacheImpl } from './dexie_db_api_impl_new';
import { pathNotInArrJudge, util, getCurrentPageEnv, getReLoginUpTime } from '@/api/util';
import { locationHelper } from '@/api/util/location_helper';
import { SubAccountExpired, getSidRegexp } from '@/const';
import { deriveCoreTrackErrorCode } from '../../../utils/coreTrackErrorCode';
const subAccountExpiredTxt = 'ERR.SUBACCOUNT.EXPIRED';
const blackList = confFunc('blackUrlList');
const config = {
    timeout: 30000,
};
export class NetworkErr extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}
export const httpClient = axios.create(config);
const MAIL_CORE_API_RESULT_EVENT_ID = 'pc_mail_core_api_result';
const MAIL_CORE_API_OK_CODE = 'S_OK';
const MAIL_CORE_FUNC_KEYS = new Set(['mbox:listMessages', 'mbox:readMessage', 'mbox:compose', 'upload:prepare', 'upload:directData', 'mbox:uploadAttach']);
const MAIL_CORE_TRACK_SAMPLE_FUNC_KEYS = new Set(['mbox:listMessages', 'mbox:readMessage', 'mbox:compose']);
const MAIL_CORE_TRACK_SAMPLE_RATE = 0.1;
const MAIL_COMPOSE_ACTION_KEYS = new Set(['continue', 'autosave', 'save', 'save_auto', 'schedule', 'deliver']);
const MAIL_CORE_PATH_RULES = [
    { matcher: /\/corp-mail\/mail\/api\/mail\/upload\/prepare\/?$/i },
    { matcher: /\/corp-mail\/mail\/api\/mail\/upload\/data\/?$/i },
    { matcher: /\/biz\/api\/biz\/attachment\/getUploadToken\/?$/i },
    { matcher: /\/biz\/api\/biz\/attachment\/finishUpload\/?$/i },
    { matcher: /\/biz\/api\/biz\/attachment\/modifyUploadContext\/?$/i },
];
const formHeaders = {
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
};
const jsonHeaders = {
    'Content-Type': 'application/json;charset=UTF-8',
};
const xmlHeaders = {
    'Content-Type': 'application/xml;charset=UTF-8',
};
const fileHeader = {
    'Content-Type': 'application/octet-stream',
};
const methodConf = {
    get: {
        body: false,
    },
    delete: {
        body: false,
    },
    head: {
        body: false,
    },
    post: {
        body: true,
    },
    put: {
        body: true,
    },
    patch: {
        body: true,
    },
};
class ReqHttpError extends Error {
    constructor(message, request, origin) {
        super(message);
        this.request = request;
        this.originRequest = origin;
    }
}
class ProcessingReq {
    constructor(o, httpApi) {
        this.reqs = [];
        this.originReq = o;
        this.httpApi = httpApi;
    }
    markRequestStart() {
        this.requestStartTime = Date.now();
    }
    getRequestDuration() {
        if (!this.requestStartTime)
            return undefined;
        const duration = Date.now() - this.requestStartTime;
        return duration >= 0 ? duration : 0;
    }
    dequeueReq(res, error) {
        this.res = res;
        this.error = error;
        if (this.reqs.length > 0) {
            let it;
            while (true) {
                it = this.reqs.pop();
                if (it) {
                    it(res, error);
                }
                else {
                    break;
                }
            }
        }
    }
    respond(res, r, error, j) {
        if (res) {
            r(res);
        }
        else if (error) {
            j(error);
        }
    }
    enqueueReq(req) {
        req.enqueued = 1;
        if (this.res) {
            return Promise.resolve(this.res);
        }
        if (this.error) {
            return Promise.reject(this.error);
        }
        return new Promise((r, j) => {
            if (!this.res && !this.error) {
                const multiple = this.originReq.reLoginProcessing ? 2 : 3;
                const timeout = req.timeout ? req.timeout * multiple : HttpImpl.MAX_CACHE_REQ_TIME * 7 * multiple;
                console.log('[http] enqueue request timeout:' + timeout, req);
                let tid = -1;
                const handler = () => {
                    if (this.res) {
                        r(this.res);
                    }
                    else if (this.error) {
                        j(this.error);
                    }
                    else {
                        const e = { massage: 'timeout', request: req, origin: this.originReq };
                        this.httpApi.dequeueReq(this, undefined, e);
                    }
                };
                tid = window.setTimeout(handler, timeout);
                this.reqs.push((res, error) => {
                    this.respond(res, r, error, j);
                    clearTimeout(tid);
                });
            }
            else {
                this.respond(this.res, r, this.error, j);
            }
        });
    }
}
const defaultConfig = {
    headers: {},
    contentType: 'form',
    responseType: 'json',
    cachePolicy: 'noCache',
};
const debugging = confFunc('disableReLogin') === '1';
const version = confFunc('version');
const lxTrafficLabel = confFunc('lxTrafficLabel') || '';
const createNewUrl = (input) => {
    if (!input)
        throw new Error('not input url');
    const urlstr = input.startsWith('http') ? input : HttpImpl.host + input;
    const url = new URL(urlstr);
    return url;
};
function getSubAccountByReqConfig(req) {
    if (req && req._account) {
        return req._account || '';
    }
    return '';
}
const isProd = confFunc('stage') === 'prod';
class HttpImpl {
    constructor() {
        this.commonHeader = {};
        this.sidRegexp = getSidRegexp();
        this.subAccountAlreadLogoutMap = {};
        this.lxTrafficLabel = lxTrafficLabel;
        this.name = apis.defaultApiImpl;
        this.eventApi = api.getEventApi();
        this.systemApi = api.getSystemApi();
        this.disableCache = false;
        this.httpCacheDb = httpCacheImpl;
        this.dataTrackerApi = api.requireLogicalApi(apis.dataTrackerApiImp);
        this.loggerApi = api.requireLogicalApi(apis.loggerApiImpl);
        this.alreadyLogout = false;
        this.storeApi = api.getDataStoreApi();
        this.performanceApi = api.requireLogicalApi(apis.performanceImpl);
        this.pageName = getPageName();
        if (inWindow()) {
            this.onWatchRouteChange();
            this.getLxTrafficLabel();
        }
    }
    normalizeApiPath(pathname) {
        const normalized = pathname.replace(/\/+$/g, '');
        return normalized || '/';
    }
    getMailCoreApiFromReqConfig(req) {
        try {
            if (!req)
                return undefined;
            const url = req.parsedUrl || (req.url ? createNewUrl(req.url) : undefined);
            if (!url)
                return undefined;
            const func = url.searchParams.get('func') || undefined;
            if (func) {
                return MAIL_CORE_FUNC_KEYS.has(func) ? func : undefined;
            }
            const pathname = this.normalizeApiPath(url.pathname || '');
            const rule = MAIL_CORE_PATH_RULES.find(item => item.matcher.test(pathname));
            return rule ? pathname : undefined;
        }
        catch (e) {
            return undefined;
        }
    }
    getMailComposeActionFromReqConfig(req) {
        var _a, _b, _c;
        try {
            const data = req === null || req === void 0 ? void 0 : req.data;
            if (!data || typeof data !== 'object')
                return undefined;
            const reqData = data;
            const candidates = [reqData.action, (_a = reqData.req) === null || _a === void 0 ? void 0 : _a.action, (_b = reqData.compose) === null || _b === void 0 ? void 0 : _b.action, (_c = reqData.data) === null || _c === void 0 ? void 0 : _c.action];
            for (const item of candidates) {
                if (typeof item === 'string' && MAIL_COMPOSE_ACTION_KEYS.has(item)) {
                    return item;
                }
            }
            return undefined;
        }
        catch (e) {
            return undefined;
        }
    }
    trackMailCoreApiResult(params) {
        try {
            const { req, result, bizCode, httpStatus, failType, duration, errorObj, reason } = params;
            const api = this.getMailCoreApiFromReqConfig(req);
            if (!api)
                return;
            if (result !== 'fail' && MAIL_CORE_TRACK_SAMPLE_FUNC_KEYS.has(api) && Math.random() >= MAIL_CORE_TRACK_SAMPLE_RATE) {
                return;
            }
            const action = api === 'mbox:compose' ? this.getMailComposeActionFromReqConfig(req) : undefined;
            const tag = req === null || req === void 0 ? void 0 : req.tag;
            const errorCode = deriveCoreTrackErrorCode({ result, failType, bizCode, httpStatus, reason, errorObj });
            const trackData = Object.assign({ api,
                tag,
                result,
                bizCode,
                httpStatus,
                failType,
                duration, _account: req === null || req === void 0 ? void 0 : req._account, enableTrackInBg: true }, (errorCode !== undefined ? { errorCode } : {}));
            this.dataTrackerApi.track(MAIL_CORE_API_RESULT_EVENT_ID, action ? Object.assign(Object.assign({}, trackData), { action }) : trackData);
        }
        catch (e) {
        }
    }
    addCommonHeader(key, value) {
        this.commonHeader[key] = value;
    }
    getLxTrafficLabel() {
        const local = this.storeApi.getSync('lxTrafficLabel', { noneUserRelated: true });
        if (local.data) {
            this.lxTrafficLabel = local.data === 'pre' ? 'pre' : '';
        }
        return this.lxTrafficLabel;
    }
    setLxTrafficLabel(value) {
        this.lxTrafficLabel = value;
        this.storeApi.putSync('lxTrafficLabel', value, { noneUserRelated: true });
    }
    removeCommonHeader(key) {
        delete this.commonHeader[key];
    }
    getDeviceInfo() {
        return this.deviceConfig;
    }
    getSubAccountByReqConfig(req) {
        return getSubAccountByReqConfig(req);
    }
    trackReLoginToHubble(res) {
        try {
            if (!process.env.BUILD_ISELECTRON) {
                return;
            }
            const pageEnv = getCurrentPageEnv();
            const upTime = getReLoginUpTime();
            if (!pageEnv)
                return;
            this.loggerApi.track('pc_login_error_and_try_re_login', {
                retryTimes: res.config.retryTimes,
                env: pageEnv,
                failedUrlPath: res.config.urlPath,
                uptime: upTime,
            });
        }
        catch (ex) {
            console.error('trackReLoginParam ex', ex);
        }
    }
    trackReLoginSuccessToHubble(res) {
        try {
            if (!process.env.BUILD_ISELECTRON)
                return;
            this.loggerApi.track('pc_login_re_login_success', {
                failedUrlPath: res.config.urlPath,
            });
        }
        catch (ex) {
            console.error('trackReLoginSuccessToHubble ex', ex);
        }
    }
    async checkAuthInfo(res) {
        try {
            if (!res || !res.config || !res.config.url)
                return false;
            const requestUrl = res.config.url;
            if (requestUrl && requestUrl.includes('sid=')) {
                return false;
            }
            const url = this.systemApi.getUrl('checkAuthInfo');
            return this.get(url, undefined, { _account: res.config._account || '' })
                .then(res => {
                if (res && res.data) {
                    const codeStr = String(res.data.code);
                    if (codeStr === '0') {
                        this.dataTrackerApi.track('second_check_auth_ok');
                        return true;
                    }
                    if (codeStr === '50010' || codeStr === '500') {
                        return false;
                    }
                    return true;
                }
                return true;
            })
                .catch(ex => {
                console.error('checkAuthInfo-http-error', ex);
                return true;
            });
        }
        catch (ex) {
            console.error('checkAuthInfo-catch-error', ex);
            return true;
        }
    }
    trackLogoutToHubble() {
        try {
            if (!process.env.BUILD_ISELECTRON)
                return;
            const upTime = getReLoginUpTime();
            this.dataTrackerApi.track('pc_login_logout_occurred', {
                uptime: upTime,
                subType: 'forcelogout',
            });
        }
        catch (ex) {
            console.error('trackLogoutToHubble ex', ex);
        }
    }
    async handleReLogin(res) {
        const ckRes = checkMasterInfo();
        const judgeWay = (ckRes === null || ckRes === void 0 ? void 0 : ckRes.isMaster) || process.env.MOCK_ENV === 'mobile' ? 'header' : 'cookie';
        if (judgeWay === 'header') {
            return Promise.reject(new Error('no need login'));
        }
        const subAccount = this.getSubAccountByReqConfig(res.config);
        const currentUser1 = this.systemApi.getCurrentUser(subAccount);
        console.warn('[http relogin] noRelogin: ', HttpImpl.noRelogin, 'currentUser: ', currentUser1);
        const alreadyLogout = !subAccount ? this.alreadyLogout : this.subAccountAlreadLogoutMap[subAccount] || false;
        const isWebmail = HttpImpl.profile.startsWith('webmail');
        let shouldReLogin = true;
        if (!isWebmail && process.env.BUILD_ISWEB && process.env.BUILD_ISLINGXI && !subAccount && isProd) {
            try {
                const cookies = await this.systemApi.doGetCookies(true);
                if (cookies) {
                    const cookie_uid = cookies.qiye_uid || '';
                    if (cookie_uid) {
                        const cookie_email = cookie_uid.replaceAll('"', '');
                        if (cookie_email !== (currentUser1 === null || currentUser1 === void 0 ? void 0 : currentUser1.id)) {
                            shouldReLogin = false;
                        }
                    }
                }
            }
            catch (ex) {
                console.error('[http] doGetCookies error', ex);
            }
        }
        if (!shouldReLogin ||
            HttpImpl.noRelogin ||
            alreadyLogout ||
            this.storeApi.isLogout(subAccount) ||
            this.systemApi.isTransferringData() ||
            this.systemApi.isBkLoginInit() ||
            this.systemApi.getIsAddAccountPage()) {
            return Promise.reject(new Error('not login'));
        }
        this.trackReLoginToHubble(res);
        if (currentUser1 == null) {
            this.triggerLogout(true, subAccount);
            return Promise.reject(new NetworkErr('ERR.ILLEGAL', 403));
        }
        this.performanceApi.time({ statKey: 're_login' }).then();
        const { config } = res;
        console.warn('[http] &&&&&& start re-login for:' + (config.urlPath || config.url) + ' result: ' + JSON.stringify(res.data), config);
        this.loggerApi.track('re_Login_in_http_request', { configPath: config.url, result: res.data });
        if (!subAccount && HttpImpl.profile && HttpImpl.profile.startsWith('webmail')) {
            this.triggerLogout(true, subAccount);
            return Promise.reject(new NetworkErr('ERR.ILLEGAL', 403));
        }
        if ((currentUser1 === null || currentUser1 === void 0 ? void 0 : currentUser1.isSharedAccount) && !process.env.BUILD_ISELECTRON) {
            this.triggerLogout(true, subAccount);
            return Promise.reject(new NetworkErr('ERR.ILLEGAL', 403));
        }
        if (!config) {
            throw new NetworkErr('ERR.ILLEGAL', 500);
        }
        if (!process.env.BUILD_ISWEB) {
            this.dataTrackerApi.track('occur_autoLogin', { reason: config.url, span: this.systemApi.getStartTimeSpan() });
        }
        const that = this;
        const reLoginProcessing = !subAccount ? HttpImpl.reLoginProcessing : HttpImpl.reLoginSubaccountProcessing[subAccount] || false;
        if (reLoginProcessing) {
            return this.enqueueRequestWhenReLogin(config);
        }
        const key = config.urlPath || '';
        let reLoginTimeMap = !subAccount ? HttpImpl.reLoginTimeMap : HttpImpl.reLoginSubAccountTimeMap[subAccount];
        if (!reLoginTimeMap && subAccount) {
            reLoginTimeMap = HttpImpl.reLoginSubAccountTimeMap[subAccount] = {};
        }
        if (reLoginTimeMap[key]) {
            reLoginTimeMap[key]++;
        }
        else {
            reLoginTimeMap[key] = 1;
        }
        const retryTime = reLoginTimeMap[key];
        if (retryTime > HttpImpl.MAX_RETRY_TIMES) {
            console.warn('[http] &&&&&& re-login failed ,max retry times exceed :' + config.url + ' for ' + config.retryTimes, config);
            reLoginTimeMap[key] = 0;
            this.triggerLogout(true, subAccount);
            this.loggerApi.track('re_login_exceed_all_max_times', config);
            return Promise.reject(new NetworkErr('ERR.LOGIN.SMSSESSEXP', 403));
        }
        if (config.retryTimes && config.retryTimes > HttpImpl.EACH_REQ_MAX_RETRY_TIMES) {
            console.warn('[http] &&&&&& re-login failed ,max retry times exceed :' + config.url + ' for ' + config.retryTimes, config);
            this.loggerApi.track('re_login_exceed_max_times', config);
            this.triggerLogout(true, subAccount);
            return Promise.reject(new NetworkErr('ERR.LOGIN.SMSSESSEXP', 403));
        }
        const checkAuthRes = await this.checkAuthInfo(res);
        if (checkAuthRes) {
            return Promise.reject(new NetworkErr('ERR.LOGIN.APITOKENFAIL', 403));
        }
        const nowTs = new Date().getTime();
        if (!subAccount) {
            HttpImpl.reLoginProcessing = true;
            HttpImpl.lastReLoginTime = nowTs;
        }
        else {
            HttpImpl.reLoginSubaccountProcessing[subAccount] = true;
            HttpImpl.lastSubAccountReLoginTime[subAccount] = nowTs;
        }
        return new Promise((r, j) => {
            let loginEventId;
            const eventName = !subAccount ? 'autoLogin' : 'subAccountLogin';
            const tid = window.setTimeout(() => {
                this.performanceApi
                    .point({
                    statKey: 're_login_ev',
                    statSubKey: 're_login_timeout-' + this.pageName,
                    value: 1,
                    valueType: 4,
                    flushAndReportImmediate: true,
                })
                    .then();
                that.eventApi.unregisterSysEventObserver(eventName, loginEventId);
                this.triggerLogout(false, subAccount);
                this.returnReLoginFailed(config, j);
                this.loggerApi.track('re_login_timeout', config);
                console.warn('[http] re-login failed for re_login_timeout ' + config.url, config);
            }, HttpImpl.MAX_RE_LOGIN_EXPIRED_TIME);
            loginEventId = that.eventApi.registerSysEventObserver(eventName, {
                name: 'httpLoginOb' + new Date().getTime(),
                func: (ev) => {
                    that.eventApi.unregisterSysEventObserver(eventName, loginEventId);
                    if (!subAccount) {
                        HttpImpl.reLoginProcessing = false;
                    }
                    else {
                        HttpImpl.reLoginSubaccountProcessing[subAccount] = false;
                    }
                    window.clearTimeout(tid);
                    console.warn('[http] &&&&&&&& finish re-login for:' + config.url + ' got ' + !!ev.eventData, config);
                    this.handleReloginPromiseExecutor.bind(this)(ev, config, r, j);
                },
            });
            if (currentUser1 == null) {
                this.triggerLogout(false, subAccount);
                this.returnReLoginFailed(config, j);
                return;
            }
            const loginExpiredEventName = !subAccount ? 'loginExpired' : 'subAccountLoginHttpExpired';
            const eventData = !subAccount ? '' : { subAccount, agentEmail: currentUser1.agentEmail, mainAccount: currentUser1.mainAccount };
            if (config.retryTimes && config.retryTimes > 1) {
                setTimeout(() => {
                    this.loggerApi.track('re_login_loginExpired_again', {
                        isBkLoginInit: this.systemApi.isBkLoginInit(),
                        isBkStableWindow: this.systemApi.isBkStableWindow(),
                    });
                    this.eventApi.sendSysEvent({
                        eventName: loginExpiredEventName,
                        eventStrData: 'warn',
                        eventData,
                        eventSeq: 0,
                    });
                }, config.retryTimes * 200 + 500);
            }
            else {
                this.loggerApi.track('re_login_loginExpired', {
                    isBkLoginInit: this.systemApi.isBkLoginInit(),
                    isBkStableWindow: this.systemApi.isBkStableWindow(),
                });
                this.eventApi.sendSysEvent({
                    eventName: loginExpiredEventName,
                    eventStrData: 'warn',
                    eventData,
                    eventSeq: 0,
                });
            }
        });
    }
    triggerCurrentUserLogout(forceLogout) {
        this.triggerLogout(forceLogout);
    }
    triggerLogout(forceLogout, subAccount) {
        const { systemApi } = this;
        const noLogoutEventPage = systemApi.getIsAddAccountPage() ||
            systemApi.isBkLoginInit() ||
            systemApi.getIsAddSubAccountPage() ||
            systemApi.getIsSubAccountInitPage() ||
            systemApi.getIsAddPersonalSubAccountPage();
        if (!subAccount && inWindow() && process.env.BUILD_ISWEB && locationHelper.isBkPage()) {
            try {
                const retrycountParam = locationHelper.getParam('retrycount');
                const retrycount = Number.isInteger(Number(retrycountParam)) ? Number(retrycountParam) : 0;
                if (retrycount < 10) {
                    setTimeout(() => {
                        const newUrl = new URL(location.href);
                        newUrl.searchParams.set('retrycount', `${retrycount + 1}`);
                        location.href = newUrl.href;
                    }, 1000);
                    return;
                }
            }
            catch (ex) {
                console.warn('[http_impl]triggerLogout refresh failed', ex);
            }
        }
        if (!noLogoutEventPage) {
            if (forceLogout || subAccount) {
                this.takeLogoutAction(subAccount);
            }
            if (subAccount)
                return;
            try {
                if (!subAccount) {
                    this.storeApi.loadUser();
                }
                else {
                    this.storeApi.loadSubAccountsUser();
                }
                const currentUser = this.systemApi.getCurrentUser(subAccount);
                if (!currentUser || !currentUser.sessionId) {
                    this.takeLogoutAction(subAccount);
                }
                else if (!subAccount) {
                    const event = {
                        eventName: 'loginRetry',
                        eventData: currentUser,
                        eventSeq: 0,
                        eventStrData: 'event',
                    };
                    this.eventApi.sendSysEvent(event);
                }
            }
            catch (e) {
                console.warn(e);
            }
        }
        else {
            console.warn('[http] logout triggered and been ignored', forceLogout);
        }
    }
    takeLogoutAction(subAccount) {
        const currentUser = this.systemApi.getCurrentUser(subAccount || '');
        const isSharedAccount = currentUser === null || currentUser === void 0 ? void 0 : currentUser.isSharedAccount;
        if (isSharedAccount) {
            this.eventApi.sendSysEvent({
                eventName: 'sharedAccountLogout',
                eventData: currentUser,
            });
        }
        if (HttpImpl.profile.startsWith('webmail') || isSharedAccount) {
            this.storeApi.setLastAccount(undefined, subAccount).then();
        }
        this.trackLogoutToHubble();
        if (!subAccount) {
            this.eventApi.sendSimpleSysEvent('logout');
            this.systemApi.unLockApp();
        }
        else {
            if (!currentUser) {
                return;
            }
            this.eventApi.sendSysEvent({
                eventName: 'subAccountLogout',
                eventData: {
                    subAccount: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
                    agentEmail: currentUser === null || currentUser === void 0 ? void 0 : currentUser.agentEmail,
                    mainAccount: currentUser === null || currentUser === void 0 ? void 0 : currentUser.mainAccount,
                },
            });
        }
    }
    enqueueRequestWhenReLogin(config) {
        console.warn('[http] &&&&&& re-login enqueue for:' + (config.urlPath || config.url), config);
        this.loggerApi.track('enqueue_http_request_when_re_login', { url: config.url, retry: config.retryTimes, data: config.data });
        return new Promise((r, j) => {
            const subAccount = this.getSubAccountByReqConfig(config);
            const tid = window.setTimeout(() => {
                this.triggerLogout(false, subAccount);
                this.returnReLoginFailed(config, j);
            }, HttpImpl.MAX_RE_LOGIN_EXPIRED_TIME);
            if (subAccount && !HttpImpl.reLoginSubAccountRequest[subAccount]) {
                HttpImpl.reLoginSubAccountRequest[subAccount] = [];
            }
            const reLoginRequest = !subAccount ? HttpImpl.reLoginRequest : HttpImpl.reLoginSubAccountRequest[subAccount];
            reLoginRequest.push((ev) => {
                this.handleReloginPromiseExecutor.bind(this)(ev, config, r, j);
                window.clearTimeout(tid);
            });
        });
    }
    handleReloginPromiseExecutor(ev, config, r, j) {
        const subAccount = this.getSubAccountByReqConfig(config);
        if (ev && ev.eventData) {
            if (ev.eventStrData && ev.eventStrData === 'timeout_event') {
                this.performanceApi
                    .point({
                    statKey: 're_login_ev',
                    statSubKey: 're_login_timeout_no_logout-' + this.pageName,
                    value: 1,
                    valueType: 4,
                    flushAndReportImmediate: true,
                })
                    .then();
                const processingReq = config.rqKey
                    ? !subAccount
                        ? HttpImpl.processingReqs[config.rqKey]
                        : HttpImpl.processingSubAccountReqs[subAccount][config.rqKey]
                    : undefined;
                this.triggerLogout(false, subAccount);
                j(this.buildTimeoutErrorReturn(config, processingReq));
            }
            else {
                if (!subAccount) {
                    HttpImpl.lastReLoginTime = Date.now();
                    this.storeApi.loadUser();
                }
                else {
                    HttpImpl.lastSubAccountReLoginTime[subAccount] = Date.now();
                    this.storeApi.loadSubAccountsUser();
                }
                const promise = this.handleRequest(config);
                this.loggerApi.track('http_request_re_login_success', { url: config.url, retry: config.retryTimes, data: config.data });
                this.performanceApi.timeEnd({ statKey: 're_login' }).then();
                r(promise);
            }
        }
        else {
            const urlPath = config.urlPath || '';
            if (!subAccount) {
                HttpImpl.reLoginTimeMap[urlPath] = 0;
            }
            else {
                if (!HttpImpl.reLoginSubAccountTimeMap[subAccount]) {
                    HttpImpl.reLoginSubAccountTimeMap[subAccount] = {};
                }
                HttpImpl.reLoginSubAccountTimeMap[subAccount][urlPath] = 0;
            }
            this.performanceApi.timeEnd({ statKey: 're_login' }, true).then();
            this.performanceApi
                .point({
                statKey: 're_login_ev',
                statSubKey: 're_login_failed-' + this.pageName,
                value: 1,
                valueType: 4,
                flushAndReportImmediate: true,
            })
                .then();
            this.triggerLogout(true, subAccount);
            this.returnReLoginFailed(config, j);
        }
        return config;
    }
    refactorRequestAfterLogin(config) {
        const { urlPath } = config;
        if (!urlPath) {
            return config;
        }
        const mapElement = HttpImpl.urlConfigMap[urlPath];
        console.log('[http] intercept ' + urlPath + ' using config :', mapElement);
        if (mapElement && mapElement.length > 0) {
            mapElement.forEach(e => {
                if (e && e.reLoginUrlHandler) {
                    config = e.reLoginUrlHandler(config);
                }
            });
        }
        else if (config && config.url && config.url.indexOf('sid=') !== -1) {
            const subAccount = this.getSubAccountByReqConfig(config);
            const currentReqUser = this.systemApi.getCurrentUser(subAccount);
            if (currentReqUser && currentReqUser.sessionId) {
                config.url = config.url.replace(this.sidRegexp, 'sid=' + currentReqUser.sessionId);
            }
        }
        return config;
    }
    returnReLoginFailed(config, j) {
        console.warn(' &&&&&&failed re-login for:' + config.url, config);
        this.loggerApi.track('http_request_re_login_failed', { url: config.url, retry: config.retryTimes, data: config.data });
        if (pathNotInArrJudge(window.location, ignoreLoginPath)) {
            console.warn('[http] will logout for request :' + config.url, config);
        }
        j(new NetworkErr('ERR.LOGIN.SMSSESSEXP', 403));
    }
    filterRequestTrack(logKey, req, data) {
        var _a;
        try {
            if ((_a = req === null || req === void 0 ? void 0 : req.parsedUrl) === null || _a === void 0 ? void 0 : _a.pathname) {
                const { parsedUrl: { pathname }, } = req;
                if (!blackList[pathname]) {
                    this.loggerApi.track(logKey, data);
                }
            }
        }
        catch (e) {
            console.error('track error', e);
        }
    }
    getAlreadyLogoutBySubAccount(subAccount) {
        if (!subAccount) {
            return this.alreadyLogout;
        }
        return this.subAccountAlreadLogoutMap[subAccount] || false;
    }
    request(method, urlPre, data, config) {
        let url = urlPre;
        if (config) {
            config._account = (data && data._subAccount ? data._subAccount : config._account) || '';
            const reqAccount = this.getSubAccountByReqConfig(config);
            if (reqAccount) {
                config._account = this.storeApi.getEmailIdByEmail(reqAccount, true);
            }
            if (config._account === SubAccountExpired) {
                return Promise.reject(subAccountExpiredTxt);
            }
        }
        config = Object.assign(Object.assign({}, defaultConfig), config);
        const subAccount = this.getSubAccountByReqConfig(config);
        const alreadyLogout = this.getAlreadyLogoutBySubAccount(subAccount);
        if ((alreadyLogout || !this.systemApi.getCurrentUser(subAccount)) && this.judgeUrlNeedAuth(url)) {
            this.sendErrorMsg(config || { url, data }, 'customer', ErrMsgCodeMap['ERR.CANCELED'], 'ERR.CANCELED');
            return Promise.reject('ERR.CANCELED.NO.AUTH');
        }
        if (HttpImpl.comNotExist === url) {
            this.sendErrorMsg(config || { url, data }, 'customer', ErrMsgCodeMap['ERR.CANCELED'], 'ERR.CANCELED');
            return Promise.reject('ERR.CANCELED');
        }
        const m = method.toLowerCase();
        if (!methodConf[m].body && data) {
            url = this.buildUrl(url, data);
        }
        else if (config.contentType === 'form') {
            data = HttpImpl.buildFormQueryContent(data);
        }
        config.url = config.url || url;
        const { deviceConfig } = this;
        if (!config.preserveOriginalUrl) {
            config.url = this.buildUrl(config.url, deviceConfig
                ? Object.assign(Object.assign(Object.assign({}, deviceConfig), { p: deviceConfig.p }), (config.appName ? { _appName: config.appName } : {})) : Object.assign({ _version: version }, (this.systemApi.getSimpleDeviceInfo(true) || {})));
        }
        this.addLxTrafficLabelToHeader(config);
        this.addLingxiLanguageToHeader(config);
        if (config.url.startsWith(HttpImpl.host) || config.url.startsWith('/')) {
            config.headers = Object.assign(Object.assign({}, this.commonHeader), config.headers);
        }
        config.data = data;
        config.method = method;
        this.getUrlPath(config);
        const key = config.urlPath || '';
        if (!HttpImpl.urlConfigMap[key] && HttpImpl.UrlConfigs) {
            config.parsedUrl && this.buildUrlInterceptor(key, config.parsedUrl.pathname);
        }
        if (config.operator) {
            const cancelSource = axios.CancelToken.source();
            const conf = config;
            conf.cancelToken = cancelSource.token;
            config.operator(operation => {
                if (operation === 'abort') {
                    config.canceled = true;
                    cancelSource.cancel(constHttpCanceledToken + JSON.stringify(data));
                }
            });
        }
        config.rqKey = this.buildRequestKey(config);
        config.seq = HttpImpl.seq.next();
        config.responseType = config.responseType || 'json';
        this.filterRequestTrack('http_request', config, config);
        config.cachePolicy = 'noCache';
        return this.handleRequest(config);
    }
    buildRequestKey(config) {
        if (config.rqKey && config.rqKey.length > 5) {
            return config.rqKey;
        }
        if (config.urlPath && HttpImpl.urlConfigMap[config.urlPath]) {
            const element = HttpImpl.urlConfigMap[config.urlPath];
            if (element.length > 0 && element[0].cachePolicyGenerator && typeof element[0].cachePolicyGenerator === 'function') {
                return element[0].cachePolicyGenerator(config).cacheKey;
            }
        }
        const mainRq = {
            url: config.url,
            data: ['stream', 'arraybuffer'].includes((config === null || config === void 0 ? void 0 : config.contentType) || '') ? new Date().getTime() : config.data,
            method: config.method,
            _account: config._account || '',
        };
        return this.systemApi.md5(JSON.stringify(mainRq), true);
    }
    getReLoginProcessingBySubAccount(subAccount) {
        if (!subAccount || !subAccount.length) {
            return HttpImpl.reLoginProcessing;
        }
        return HttpImpl.reLoginSubaccountProcessing[subAccount] || false;
    }
    handleRequest(config) {
        var _a;
        if (!config.rqKey || !config.url) {
            return Promise.reject(new Error('illegal.request'));
        }
        const subAccount = this.getSubAccountByReqConfig(config);
        config.retryTimes = config.retryTimes ? config.retryTimes + 1 : 1;
        if (subAccount && !HttpImpl.processingSubAccountReqs[subAccount]) {
            HttpImpl.processingSubAccountReqs[subAccount] = {};
        }
        const inProcessReq = !subAccount ? config.rqKey in HttpImpl.processingReqs : config.rqKey in HttpImpl.processingSubAccountReqs[subAccount];
        if (inProcessReq && !config.noEnqueue) {
            const prq = !subAccount ? HttpImpl.processingReqs[config.rqKey] : HttpImpl.processingSubAccountReqs[subAccount][config.rqKey];
            if (prq.originReq.seq !== config.seq) {
                return prq.enqueueReq(config);
            }
        }
        const processingReq = new ProcessingReq(config, this);
        if (config.useCacheResultPeriod &&
            config.useCacheResultPeriod > HttpImpl.MAX_CACHE_REQ_TIME &&
            (config.cachePolicy === 'useDirect' || config.cachePolicy === 'useAndRefresh') &&
            !config.cacheMissed &&
            !this.disableCache) {
            return this.useCachedResult(processingReq, undefined, config);
        }
        if (!subAccount) {
            HttpImpl.processingReqs[config.rqKey] = processingReq;
        }
        else {
            HttpImpl.processingSubAccountReqs[subAccount][config.rqKey] = processingReq;
        }
        HttpImpl.initHttpContentType(config);
        const reLoginProcessing = this.getReLoginProcessingBySubAccount(subAccount);
        if (reLoginProcessing && this.judgeUrlNeedAuth(config.url)) {
            return this.enqueueRequestWhenReLogin(config);
        }
        const lastReLoginTime = !subAccount ? HttpImpl.lastReLoginTime : HttpImpl.lastSubAccountReLoginTime[subAccount] || 0;
        if (lastReLoginTime && lastReLoginTime + 2 * HttpImpl.MAX_RE_LOGIN_EXPIRED_TIME > Date.now()) {
            config = this.refactorRequestAfterLogin(config);
            console.log('[http] refactor request http: ' + (((_a = config === null || config === void 0 ? void 0 : config.url) === null || _a === void 0 ? void 0 : _a.replace(HttpImpl.host, '')) || '[error]'), config);
            this.loggerApi.track('refactor_request_when_re_login', { url: config.url, retry: config.retryTimes, data: config.data });
        }
        this.collectRequest((config === null || config === void 0 ? void 0 : config.urlPath) || (config === null || config === void 0 ? void 0 : config.url));
        return this.addTokenToHeader(config).then(newConfig => {
            processingReq.markRequestStart();
            return httpClient
                .request(newConfig)
                .then(res => this.buildApiResponse(res, processingReq))
                .catch((error) => this.handleError(error, processingReq));
        });
    }
    getLoginAccountHeader(account) {
        var _a;
        const curUser = this.systemApi.getCurrentUser(account);
        const res = {
            coremail: (_a = curUser) === null || _a === void 0 ? void 0 : _a.coremail,
            'qiye-sess': curUser === null || curUser === void 0 ? void 0 : curUser.sessionId,
            'qiye-token': curUser === null || curUser === void 0 ? void 0 : curUser.qiyeToken,
            'qiye-uid': curUser === null || curUser === void 0 ? void 0 : curUser.id,
            qiye_token: curUser === null || curUser === void 0 ? void 0 : curUser.qiyeToken,
            qiye_uid: curUser === null || curUser === void 0 ? void 0 : curUser.id,
        };
        try {
            if (res) {
                return res;
            }
        }
        catch (error) {
            return {};
        }
        return {};
    }
    async addTokenToHeader(requestConfig) {
        var _a;
        const targetUrl = requestConfig.url;
        const href = (_a = requestConfig === null || requestConfig === void 0 ? void 0 : requestConfig.parsedUrl) === null || _a === void 0 ? void 0 : _a.href;
        if (!process.env.BUILD_ISELECTRON && targetUrl) {
            const filter = {
                urls: [HttpImpl.host, confFunc('webMailHZHost') + '', confFunc('webMailBJHost') + ''],
            };
            const isValidUrl = filter.urls.some(url => targetUrl.startsWith(url) || (href === null || href === void 0 ? void 0 : href.startsWith(url)));
            if (isValidUrl) {
                const subAccount = this.getSubAccountByReqConfig(requestConfig);
                const lastReLoginTime = !subAccount ? HttpImpl.lastReLoginTime : HttpImpl.lastSubAccountReLoginTime[subAccount] || 0;
                const needRefresh = !!(lastReLoginTime && lastReLoginTime + 2 * HttpImpl.MAX_RE_LOGIN_EXPIRED_TIME > Date.now());
                let cookies = (await this.systemApi.doGetCookies(needRefresh, requestConfig._account)) || {};
                if (cookies && !subAccount) {
                    cookies = {};
                }
                const ckRes = checkMasterInfo();
                const judgeWay = (ckRes === null || ckRes === void 0 ? void 0 : ckRes.isMaster) || process.env.MOCK_ENV === 'mobile' ? 'header' : 'cookie';
                if (judgeWay === 'cookie') {
                    if (cookies) {
                        if (subAccount && (!cookies['qiye-token'] || !cookies.coremail)) {
                            return Promise.reject(subAccountExpiredTxt);
                        }
                        const originHeaders = requestConfig.headers || {};
                        requestConfig.headers = Object.assign(Object.assign({}, originHeaders), cookies);
                    }
                }
                else {
                    await this.storeApi.initUserAuthReady;
                    const storeHeaders = this.getLoginAccountHeader(requestConfig._account);
                    if (storeHeaders) {
                        const originHeaders = requestConfig.headers || {};
                        requestConfig.headers = Object.assign(Object.assign({}, originHeaders), storeHeaders);
                    }
                }
            }
        }
        else if (requestConfig._account) {
            const _session = await this.systemApi.getSessionNameOfSubAccount(requestConfig._account);
            if (requestConfig.url && !locationHelper.getSessioNameByUrl(requestConfig.url, _session)) {
                requestConfig.url = this.buildUrl(requestConfig.url, { _session });
            }
        }
        return requestConfig;
    }
    addLxTrafficLabelToHeader(requestConfig) {
        if (this.lxTrafficLabel && requestConfig.url) {
            const url = requestConfig.url.startsWith('http') ? requestConfig.url : HttpImpl.host + requestConfig.url;
            const list = ['https://waimao.office.163.com', 'https://waimao-pre.office.163.com'];
            if (list.some(v => url.startsWith(v))) {
                const originHeaders = requestConfig.headers || {};
                requestConfig.headers = Object.assign(Object.assign({}, originHeaders), { 'lx-traffic-label': this.lxTrafficLabel });
            }
        }
        return requestConfig;
    }
    addLingxiLanguageToHeader(requestConfig) {
        const url = requestConfig.url || '';
        let shouldAdd = false;
        if (isElectron()) {
            shouldAdd = url.startsWith(HttpImpl.host);
        }
        else {
            const isRelativePath = url.charAt(0) === '/' && url.charAt(1) !== '/';
            shouldAdd = isRelativePath || url.startsWith(HttpImpl.host);
        }
        if (!shouldAdd)
            return requestConfig;
        const { systemApi } = this;
        const lang = systemApi.getSystemLang();
        const originHeaders = requestConfig.headers || {};
        requestConfig.headers = Object.assign(Object.assign({}, originHeaders), { 'Lingxi-Language': lang });
        return requestConfig;
    }
    handleError(error, processingReq) {
        var _a, _b, _c;
        this.loggerApi.track('http_request_failed', error);
        console.warn('[http] request got error:' + processingReq.originReq.url, error, error.response, error.request, processingReq.originReq);
        const requestDuration = processingReq.getRequestDuration();
        if (error.response) {
            const res = error.response;
            if (Number(res.status) === 401) {
                return this.handleReLogin(res);
            }
            let code;
            let title;
            try {
                if (res.data && typeof res.data === 'object') {
                    const data = res.data;
                    const ntcode = data.code ||
                        data.msgCode ||
                        ((_a = data.data) === null || _a === void 0 ? void 0 : _a.code) ||
                        ((_b = data.data) === null || _b === void 0 ? void 0 : _b.msgCode);
                    const codeAvailable = ntcode in ErrMsgCodeMap;
                    title = codeAvailable ? ErrMsgCodeMap[ntcode] : data.msg || data.msgCodeDesc || title;
                    code = codeAvailable ? ntcode : code;
                    if (code === 'FS_UNKNOWN') {
                        this.loggerApi.track('occur_unknown_error', { span: this.systemApi.getStartTimeSpan(), code });
                    }
                }
            }
            catch (e) {
                console.warn(e);
            }
            if (res.status == 502 || res.status == 503 || res.status == 504) {
                this.sendErrorMsg(processingReq.originReq, 'toast', ErrMsgCodeMap['SERVER.ERR'], 'SERVER.ERR');
                this.dequeueReq(processingReq, undefined, error);
                return this.buildTimeoutErrorReturn(processingReq.originReq, processingReq, error);
            }
            if (res.status == 500 || res.status >= 505) {
                if (title) {
                    this.sendErrorMsg(processingReq.originReq, 'toast', title || ErrMsgCodeMap['SERVER.ERR'], code || 'SERVER.ERR');
                }
                this.loggerApi.track('occur_unknown_error', { span: this.systemApi.getStartTimeSpan(), code: '500' });
                this.dequeueReq(processingReq, undefined, error);
                this.trackMailCoreApiResult({
                    req: processingReq.originReq,
                    result: 'fail',
                    httpStatus: Number(res.status),
                    failType: 'http',
                    duration: requestDuration,
                    errorObj: error,
                    reason: typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : undefined,
                });
                return Promise.reject(res);
            }
            if (res.status != 401 && res.status >= 400 && res.status < 500) {
                if (title) {
                    this.sendErrorMsg(processingReq.originReq, 'toast', title || ErrMsgCodeMap['ERR.PARAM'], code || 'ERR.PARAM');
                }
                this.loggerApi.track('occur_unknown_error', {
                    span: this.systemApi.getStartTimeSpan(),
                    code: res.status,
                });
                this.dequeueReq(processingReq, undefined, error);
                this.trackMailCoreApiResult({
                    req: processingReq.originReq,
                    result: 'fail',
                    httpStatus: Number(res.status),
                    failType: 'http',
                    duration: requestDuration,
                    errorObj: error,
                    reason: typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : undefined,
                });
                return Promise.reject(res);
            }
            if (environment !== 'prod') {
                this.sendErrorMsg(processingReq.originReq, 'toast', '发生未知的服务器错误', code || 'SERVER.ERR');
            }
            this.dequeueReq(processingReq, undefined, error);
            this.trackMailCoreApiResult({
                req: processingReq.originReq,
                result: 'fail',
                httpStatus: Number(res.status),
                failType: 'http',
                duration: requestDuration,
                errorObj: error,
                reason: typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : undefined,
            });
            return Promise.reject(res);
        }
        if (error.request) {
            const isCanceled = (typeof axios.isCancel === 'function' && axios.isCancel(error)) ||
                (error === null || error === void 0 ? void 0 : error.code) === 'ERR_CANCELED' ||
                (typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' && error.message.startsWith(constHttpCanceledToken));
            if (isCanceled) {
                this.sendErrorMsg(processingReq.originReq, 'customer', ErrMsgCodeMap['ERR.CANCELED'], 'ERR.CANCELED', '');
                this.dequeueReq(processingReq, undefined, error);
                this.trackMailCoreApiResult({
                    req: processingReq.originReq,
                    result: 'canceled',
                    failType: 'unknown',
                    duration: requestDuration,
                    errorObj: error,
                    reason: typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : undefined,
                });
                return Promise.reject(error);
            }
            this.sendErrorMsg(processingReq.originReq, 'toast', '网络错误', 'NETWORK.ERR', ErrMsgCodeMap['NETWORK.ERR']);
            this.dequeueReq(processingReq, undefined, error);
            return this.buildTimeoutErrorReturn((_c = error.request) === null || _c === void 0 ? void 0 : _c.config, processingReq, error);
        }
        this.sendErrorMsg(processingReq.originReq, 'customer', ErrMsgCodeMap['ERR.CANCELED'], 'ERR.CANCELED', '');
        console.warn('[http] canceled?', error);
        this.dequeueReq(processingReq, undefined, error);
        this.trackMailCoreApiResult({
            req: processingReq.originReq,
            result: 'canceled',
            failType: 'unknown',
            duration: requestDuration,
            errorObj: error,
            reason: typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : undefined,
        });
        return Promise.reject(error);
    }
    buildTimeoutErrorReturn(conf, processingReq, error) {
        var _a, _b, _c;
        console.log('[http] timeout for request :' + ((_a = processingReq === null || processingReq === void 0 ? void 0 : processingReq.originReq) === null || _a === void 0 ? void 0 : _a.url), error, conf, processingReq);
        const reason = 'NETWORK.ERR.TIMEOUT';
        const trackReq = (processingReq === null || processingReq === void 0 ? void 0 : processingReq.originReq) || conf;
        let failType = 'unknown';
        try {
            if ((error === null || error === void 0 ? void 0 : error.code) === 'ECONNABORTED' ||
                String((error === null || error === void 0 ? void 0 : error.message) || '')
                    .toLowerCase()
                    .includes('timeout')) {
                failType = 'timeout';
            }
            else if ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) {
                failType = 'http';
            }
            else if (error === null || error === void 0 ? void 0 : error.request) {
                failType = 'network';
            }
        }
        catch (e) {
        }
        this.trackMailCoreApiResult({
            req: trackReq,
            result: 'fail',
            httpStatus: ((_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.status) ? Number(error.response.status) : undefined,
            failType,
            duration: processingReq === null || processingReq === void 0 ? void 0 : processingReq.getRequestDuration(),
            errorObj: error,
            reason: typeof (error === null || error === void 0 ? void 0 : error.message) === 'string' ? error.message : undefined,
        });
        if (processingReq)
            this.dequeueReq(processingReq, undefined, reason);
        return Promise.reject(reason);
    }
    sendErrorMsg(config, type, title, code, content) {
        var _a;
        try {
            if (environment !== 'prod') {
                this.eventApi.sendSysEvent({
                    eventSeq: 0,
                    eventName: 'error',
                    eventLevel: 'error',
                    eventStrData: '',
                    eventData: {
                        popupType: config.noErrorMsgEmit ? 'customer' : type,
                        popupLevel: 'error',
                        title,
                        content: (content || '') +
                            (environment === 'prod'
                                ? ''
                                : ((_a = config.url) === null || _a === void 0 ? void 0 : _a.replace(HttpImpl.host, '')) + '||' + (typeof config.data === 'object' ? JSON.stringify(config.data) : config.data)),
                        code,
                    },
                    auto: true,
                });
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    getCacheFailedTimeBySubAccount(subAccount) {
        const cacheFailedTime = !subAccount ? HttpImpl.cacheFailedTime : HttpImpl.cacheSubAccountFailedTime[subAccount];
        return cacheFailedTime || 0;
    }
    setCacheFailedTimeBySubAccount(newFailedTime, subAccount) {
        if (!subAccount) {
            HttpImpl.cacheFailedTime = newFailedTime;
        }
        else {
            HttpImpl.cacheSubAccountFailedTime[subAccount] = newFailedTime;
        }
    }
    useCachedResult(processingReq, error, req) {
        const subAccount = this.getSubAccountByReqConfig(req);
        const config = req || processingReq.originReq;
        if (config.cacheMissed) {
            return this.useCacheResultFailed(processingReq, error, req);
        }
        console.log('[http] start use cache:' + processingReq.originReq.url);
        if (!subAccount && !this.systemApi.getCurrentUser()) {
            return this.useCacheResultFailed(processingReq, error, req);
        }
        if (subAccount && !this.systemApi.getCurrentUser(subAccount)) {
            return this.useCacheResultFailed(processingReq, error, req);
        }
        return this.httpCacheDb
            .get(req)
            .then((res) => {
            if (res && res.content && res.expiredTime && res.createTime) {
                const expired = Number(res.expiredTime);
                const created = Number(res.createTime);
                const now = new Date().getTime();
                const expirePeriod = req.useCacheResultPeriod || HttpImpl.MAX_CACHE_REQ_TIME + 500;
                const canUse = error || (expired > now && created + expirePeriod > now);
                if (canUse) {
                    try {
                        const parse = JSON.parse(res.content);
                        if (parse && parse.status && parse.status >= 200 && parse.status <= 210) {
                            req.useCacheReturn = true;
                            processingReq.originReq.useCacheReturn = true;
                            this.dequeueReq(processingReq, parse, undefined);
                            const cacheFailedTime = this.getCacheFailedTimeBySubAccount(subAccount);
                            this.setCacheFailedTimeBySubAccount(cacheFailedTime < 0 ? 0 : HttpImpl.cacheFailedTime - 1, subAccount);
                            return parse;
                        }
                    }
                    catch (e) {
                        console.warn('use cache failed for exception ', e);
                    }
                }
            }
            return this.useCacheResultFailed(processingReq, error, req);
        })
            .catch(rs => {
            const cacheFailedTime = this.getCacheFailedTimeBySubAccount(subAccount);
            if (cacheFailedTime > HttpImpl.MAX_CACHE_FAILED_TIMES / 2 + 1) {
                this.setDisableCache(true);
                console.log('[http] use cache failed too much,will close cache function ' + HttpImpl.cacheFailedTime);
            }
            return this.useCacheResultFailed(processingReq, rs, error, req);
        });
    }
    useCacheResultFailed(processingReq, reason, error, req) {
        const subAccount = this.getSubAccountByReqConfig(req);
        console.log('[http] use cache failed for ' + processingReq.originReq.url, reason, error, req);
        const cacheFailedTime = this.getCacheFailedTimeBySubAccount(subAccount);
        const newCacheFailedTime = cacheFailedTime > HttpImpl.MAX_CACHE_FAILED_TIMES ? HttpImpl.MAX_CACHE_FAILED_TIMES : cacheFailedTime + 1;
        this.setCacheFailedTimeBySubAccount(newCacheFailedTime, subAccount);
        req && (req.cacheMissed = true);
        processingReq.originReq.cacheMissed = true;
        if (error) {
            this.dequeueReq(processingReq, undefined, error);
            return Promise.reject(error);
        }
        return this.handleRequest(req || processingReq.originReq);
    }
    makeSubAccountMapSafe(map, subAccount) {
        if (!map[subAccount]) {
            map[subAccount] = {};
        }
    }
    buildApiResponse(res, processingReq) {
        var _a, _b;
        const apiConfig = res.config;
        const requestDuration = processingReq.getRequestDuration();
        if (apiConfig.canceled) {
            throw new Error('ERR.CANCELED');
        }
        if (res.status == 401) {
            return this.handleReLogin(res);
        }
        try {
            const uri = res.config.url;
            const req = processingReq.originReq;
            const realData = this.getAndParseData(res, req);
            const subAccount = this.getSubAccountByReqConfig(apiConfig);
            const ret = {
                uri,
                config: res.config,
                data: (req.responseType === 'json' || req.expectedResponseType === 'json') && realData,
                rawData: req.responseType !== 'json' && req.expectedResponseType !== 'json' && realData,
                headers: res.headers,
                status: res.status,
                statusText: res.statusText,
            };
            let mapElement;
            let key;
            if (uri) {
                key = apiConfig.urlPath || '';
                mapElement = HttpImpl.urlConfigMap[key];
            }
            if (mapElement && this.testReLoginPolicy(mapElement, ret)) {
                return this.handleReLogin(res);
            }
            if ((res.status >= 200 && res.status <= 210) || res.status == 302 || res.status == 304 || res.status == 307) {
                if (key && HttpImpl.reLoginTimeMap[key]) {
                    if (!subAccount) {
                        HttpImpl.reLoginTimeMap[key] = 0;
                    }
                    else {
                        this.makeSubAccountMapSafe(HttpImpl.reLoginSubAccountTimeMap, subAccount);
                        HttpImpl.reLoginSubAccountTimeMap[subAccount][key] = 0;
                    }
                }
            }
            processingReq.config = mapElement;
            this.dequeueReq(processingReq, ret, undefined);
            const rawBizCode = (_a = ret.data) === null || _a === void 0 ? void 0 : _a.code;
            const bizCode = typeof rawBizCode === 'string' || typeof rawBizCode === 'number' ? String(rawBizCode) : undefined;
            if (bizCode !== undefined) {
                const isBizSuccess = bizCode === MAIL_CORE_API_OK_CODE || bizCode === '0';
                this.trackMailCoreApiResult({
                    req: processingReq.originReq,
                    result: isBizSuccess ? 'success' : 'fail',
                    bizCode,
                    httpStatus: Number(ret.status),
                    failType: isBizSuccess ? undefined : 'biz',
                    duration: requestDuration,
                });
            }
            if (ret.config.retryTimes && ret.config.retryTimes > 1) {
                this.trackReLoginSuccessToHubble(ret);
            }
            return ret;
        }
        catch (e) {
            console.error('[http] error found when request ' + ((_b = res === null || res === void 0 ? void 0 : res.config) === null || _b === void 0 ? void 0 : _b.url), e);
            this.loggerApi.track('http-request-error-caught', { processingReq, res });
            const parseErrMessage = e instanceof Error ? e.message : typeof e === 'string' ? e : undefined;
            this.trackMailCoreApiResult({
                req: processingReq === null || processingReq === void 0 ? void 0 : processingReq.originReq,
                result: 'fail',
                httpStatus: Number(res === null || res === void 0 ? void 0 : res.status),
                failType: 'parse',
                duration: requestDuration,
                errorObj: e,
                reason: parseErrMessage,
            });
            this.sendErrorMsg(res.config, 'toast', '接口返回数据有误，无法解析，请稍后再试', 'ERR.SERVER');
            return Promise.reject('ERR.SERVER');
        }
    }
    getAndParseData(res, originReq) {
        if (!res || res.data === undefined) {
            return null;
        }
        this.filterRequestTrack('http_request_return', originReq, {
            status: res.status,
            data: !!res.data,
            headers: res.headers,
            config: originReq,
        });
        if (originReq.responseType === 'text' && typeof res.data === 'string' && originReq.expectedResponseType === 'json') {
            res.data = String(res.data);
            const dt = res.data.substr(res.data.indexOf('{'));
            console.log('[http] not regular json to handle:', dt);
            try {
                res.data = dt;
                return JSON.parse(res.data);
            }
            catch (e) {
                console.warn('http request not valid json ,return ' + dt, e);
            }
            try {
                return (new Function('var window={},document={};return (function(){return ' + dt + ';})()').bind({})());
            }
            catch (e) {
                console.warn('http request not valid json, can not parse ,return ' + dt, e);
                throw e;
            }
        }
        if (originReq.responseType === 'text' && originReq.expectedResponseType === 'text') {
            return typeof res.data === 'string' || typeof res.data === 'number' ? String(res.data) : JSON.stringify(res.data);
        }
        return res.data;
    }
    dequeueReq(processingReq, ret, error) {
        processingReq.dequeueReq(ret, error);
        const { originReq } = processingReq;
        const reqKey = originReq.rqKey || '';
        const subAccount = originReq._account || '';
        if (ret) {
            setTimeout(() => {
                if (!subAccount) {
                    delete HttpImpl.processingReqs[reqKey];
                }
                else {
                    delete (HttpImpl.processingSubAccountReqs[subAccount] || {})[reqKey];
                }
                if (!processingReq.originReq.useCacheReturn || processingReq.originReq.cachePolicy === 'useAndRefresh') {
                    this.handleCache(processingReq);
                }
            }, HttpImpl.MAX_CACHE_REQ_TIME);
        }
        else {
            if (!subAccount) {
                delete HttpImpl.processingReqs[reqKey];
            }
            else {
                delete (HttpImpl.processingSubAccountReqs[subAccount] || {})[reqKey];
            }
            if (error && error instanceof ReqHttpError) {
                console.warn('error when handle request:', error);
            }
        }
    }
    handleCache(req) {
        const subAccount = req.originReq._account || '';
        if (req && req.originReq && req.originReq.cachePolicy === 'useAndRefresh') {
            const newConf = Object.assign(Object.assign({}, req.originReq), { cachePolicy: 'refresh' });
            newConf.useCacheReturn = false;
            this.handleRequest(newConf)
                .then(() => {
                console.log('[http] refresh cache done');
            })
                .catch(re => {
                console.warn('catch error when write http cache', re);
            });
        }
        const needCache = !!this.systemApi.getCurrentUser(subAccount) && req.originReq.cachePolicy !== 'noCache';
        const resultReady = req && req.originReq && req.res;
        let canCache = true;
        if (req.originReq.urlPath && HttpImpl.urlConfigMap[req.originReq.urlPath] && resultReady) {
            const element = HttpImpl.urlConfigMap[req.originReq.urlPath];
            canCache = element.some(it => it && it.canCache && it.canCache(req.res));
        }
        if (needCache && canCache && resultReady && !req.originReq.useCacheReturn && !req.originReq.requestFailed) {
            this.httpCacheDb.put(req.originReq, req.res).then().catch(console.error);
        }
        if (needCache && resultReady && req.originReq.useCacheReturn && (req.originReq.cacheMissed || req.originReq.requestFailed || !canCache)) {
            this.cleanCacheEntry(req.originReq);
        }
    }
    delete(url, data, config) {
        return this.request('delete', url, data, config);
    }
    get(url, data, config) {
        return this.request('get', url, data, config);
    }
    head(url, data, config) {
        return this.request('head', url, data, config);
    }
    options(url, config) {
        return this.request('options', url, undefined, config);
    }
    patch(url, data, config) {
        return this.request('patch', url, data, config);
    }
    post(url, data, config) {
        return this.request('post', url, data, config);
    }
    put(url, data, config) {
        return this.request('put', url, data, config);
    }
    async appHostRequest(data) {
        var _a;
        const method = (data === null || data === void 0 ? void 0 : data.method) || 'ui.calendarEvent';
        const params = (data === null || data === void 0 ? void 0 : data.params) || {};
        if ((_a = window) === null || _a === void 0 ? void 0 : _a.appHostRequest) {
            throw new Error('appHostRequest is not defined');
        }
        const paramsStr = JSON.stringify(params);
        const obj = { method, params: paramsStr };
        const objStr = JSON.stringify(obj);
        const reqStr = objStr.replace(/\\"/g, '\\\\"');
        console.log('appHostRequest request', reqStr);
        return new Promise((resolve, reject) => {
            window.appHostRequest({
                request: reqStr,
                persistent: false,
                onSuccess(data) {
                    console.log('appHostRequest onSuccess', data);
                    resolve({ suc: true, data });
                },
                onFailure(errCode, errMessage) {
                    console.log('appHostRequest onFailure', errCode, errMessage);
                    reject({
                        suc: false,
                        errCode,
                        errMessage,
                    });
                },
            });
        });
    }
    addConfig(conf) {
        if (conf) {
            const had = HttpImpl.UrlConfigs.findIndex(item => util.regexEqual(item.matcher, conf.matcher));
            if (had < 0) {
                HttpImpl.UrlConfigs.push(conf);
            }
        }
    }
    cleanCacheEntry(req) {
        if (!req.rqKey) {
            req.rqKey = this.buildRequestKey(config);
        }
        if (req.rqKey) {
            console.log('[http] cache missed, and delete ', req.url);
            this.httpCacheDb
                .deleteItem(req.rqKey)
                .then()
                .catch(err => {
                console.error(err);
            });
        }
    }
    static buildFormQueryContent(data, addHost, url) {
        const curDomain = locationHelper.getHost();
        let ret = addHost && !isElectron() ? '_host=' + encodeURIComponent(curDomain) : '';
        if (data && typeof data === 'object') {
            Object.keys(data).forEach(i => {
                if (i === '_session' && !data[i]) {
                    return;
                }
                if (typeof data[i] !== 'undefined' && i !== '_host') {
                    if (url && url.includes(`${i}=`)) {
                        return;
                    }
                    ret += ret.length > 0 ? '&' : '';
                    ret = ret + i + '=' + this.paramToString(data[i]);
                }
            });
        }
        return ret;
    }
    static paramToString(datum) {
        if (typeof datum === 'string' || typeof datum === 'number') {
            return encodeURIComponent(datum);
        }
        if (typeof datum === 'boolean') {
            return datum ? 'true' : 'false';
        }
        if (typeof datum === 'object') {
            const isArray = Array.isArray(datum);
            if (!isArray) {
                return encodeURIComponent(JSON.stringify(datum));
            }
            let ret = '';
            let first = true;
            datum.forEach(i => {
                if (first) {
                    first = false;
                }
                else {
                    ret += ',';
                }
                ret += this.paramToString(i);
            });
            return ret;
        }
        if (typeof datum === 'symbol' || typeof datum === 'function') {
            return encodeURIComponent(String(datum));
        }
        return '';
    }
    buildUrl(url, req, conf) {
        const hasParam = url.indexOf('?') > 0;
        if (hasParam && !url.endsWith('&') && !url.endsWith('?')) {
            url += '&';
        }
        if (!hasParam) {
            url += '?';
        }
        return url + HttpImpl.buildFormQueryContent(req, url.indexOf('_host=') < 0 && !(conf === null || conf === void 0 ? void 0 : conf.noAddingHost), url);
    }
    static initHttpContentType(config) {
        if (config.contentType === '') {
            Object.assign(config.headers, { 'Content-Type': '' });
        }
        else {
            config.contentType = config.contentType || 'form';
            if (config.contentType === 'form') {
                Object.assign(config.headers, formHeaders);
            }
            else if (config.contentType === 'json') {
                config.headers = Object.assign(Object.assign({}, config.headers), jsonHeaders);
            }
            else if (config.contentType === 'xml') {
                Object.assign(config.headers, xmlHeaders);
            }
            else if (config.contentType === 'stream') {
                if (config.headers['Content-Type'] && config.headers['Content-Type'].length > 0) {
                    console.log('[http] content type original:', config.headers['Content-Type']);
                }
                else {
                    Object.assign(config.headers, fileHeader);
                }
            }
        }
        Object.assign(config.headers, { Accept: 'application/json' });
    }
    buildUrlInterceptor(urlPath, urlStr) {
        HttpImpl.urlConfigMap[urlPath] = HttpImpl.urlConfigMap[urlPath] || [];
        HttpImpl.UrlConfigs.forEach(it => {
            if (it.matcher.test(urlStr)) {
                const confg = HttpImpl.urlConfigMap[urlPath];
                confg.push(it);
            }
        });
    }
    testReLoginPolicy(mapElement, ret) {
        if (!mapElement || mapElement.length === 0) {
            return false;
        }
        return mapElement.some(el => el && el.requestAutoReLogin && el.requestAutoReLogin(ret));
    }
    getUrlPath(config) {
        if (!config || !config.url) {
            return;
        }
        const { url } = config;
        config.parsedUrl = createNewUrl(url);
        const urlStr = config.parsedUrl.pathname + (config.tag || '');
        config.urlPath = urlStr;
    }
    onBlur() {
        HttpImpl.focused = false;
        return this.name;
    }
    onFocus() {
        HttpImpl.focused = true;
        return this.name;
    }
    setLogoutStatus(flag) {
        this.alreadyLogout = flag;
    }
    setSubAccountLogoutStatus(subAccount, flag) {
        this.subAccountAlreadLogoutMap[subAccount] = flag;
    }
    watchLoginExpired() {
        HttpImpl.reLoginProcessing = true;
    }
    watchSubAccountLoginExpired(ev) {
        if (ev && ev.eventData) {
            const { eventData } = ev;
            const emailId = eventData.subAccount;
            HttpImpl.reLoginSubaccountProcessing[emailId] = true;
        }
    }
    handleAutoLogin(ev) {
        if (ev && ev.eventData) {
            HttpImpl.reLoginProcessing = false;
            HttpImpl.cacheFailedTime = 0;
            HttpImpl.reLoginTimeMap = {};
            HttpImpl.processingReqs = {};
            this.alreadyLogout = false;
            HttpImpl.lastReLoginTime = new Date().getTime();
            let it;
            while (true) {
                it = HttpImpl.reLoginRequest.pop();
                if (it) {
                    it(ev);
                }
                else {
                    break;
                }
            }
            this.buildLogoutPage();
        }
        this.httpCacheDb.init();
        this.startLaunchCollect();
    }
    afterLogin(ev) {
        if (ev && ev.data) {
            this.handleAutoLogin(ev.data);
        }
        this.httpCacheDb.init();
        return this.name;
    }
    resetSubAccountState(subAccount) {
        HttpImpl.reLoginSubaccountProcessing[subAccount] = false;
        this.setCacheFailedTimeBySubAccount(0, subAccount);
        HttpImpl.reLoginSubAccountTimeMap[subAccount] = {};
        HttpImpl.processingSubAccountReqs[subAccount] = {};
        this.subAccountAlreadLogoutMap[subAccount] = false;
        HttpImpl.lastSubAccountReLoginTime[subAccount] = new Date().getTime();
    }
    handleSubAccountLogin(ev) {
        if (ev && ev.eventData) {
            const { id: subAccount } = ev.eventData;
            if (!subAccount)
                return;
            this.resetSubAccountState(subAccount);
            let it;
            while (true) {
                it = (HttpImpl.reLoginSubAccountRequest[subAccount] || []).pop();
                if (it) {
                    it(ev);
                }
                else {
                    break;
                }
            }
        }
    }
    beforeLogout() {
        this.httpCacheDb.close();
        this.cancelRequestCollect();
        return this.name;
    }
    setDisableCache(disable) {
        this.disableCache = disable;
    }
    getPageModule(url) {
        const matched = url.match(/#(.+?)(?=\?|$)/);
        return matched && matched[1] ? matched[1] : '';
    }
    onWatchRouteChange() {
        if (inWindow()) {
            window.addEventListener('routeChange', e => {
                var _a;
                const event = e;
                const prevModule = ((_a = event.detail.prevLocation) === null || _a === void 0 ? void 0 : _a.href) ? this.getPageModule(event.detail.prevLocation.href) : '/';
                const currentModule = this.getPageModule(event.detail.location.href);
                const isDifferentModule = prevModule !== currentModule && currentModule !== HttpImpl.routeCollect.currentUrl;
                console.warn('[request collect] isDifferentModule ', isDifferentModule);
                if (isDifferentModule) {
                    if (HttpImpl.routeCollect.isCollecting) {
                        console.warn('[request collect] route break by route change');
                    }
                    if (HttpImpl.launchCollect.isCollecting) {
                        HttpImpl.launchCollect.launchUrlSet.add(currentModule);
                    }
                    if (HttpImpl.launch15Collect.isCollecting) {
                        HttpImpl.launch15Collect.launchUrlSet.add(currentModule);
                    }
                    clearTimeout(HttpImpl.routeCollect.timer);
                    HttpImpl.routeCollect.currentUrl = currentModule;
                    HttpImpl.routeCollect.timer = 0;
                    this.startRouteCollect();
                }
            });
        }
    }
    collectRequest(requestPath = '') {
        if (!inWindow()) {
            return;
        }
        if (HttpImpl.launchCollect.isCollecting) {
            HttpImpl.launchCollect.num += 1;
            const curPath = HttpImpl.launchCollect.requestPathMap.get(requestPath);
            if (!curPath) {
                HttpImpl.launchCollect.requestPathMap.set(requestPath, 1);
            }
            else {
                HttpImpl.launchCollect.requestPathMap.set(requestPath, curPath + 1);
            }
        }
        if (HttpImpl.launch15Collect.isCollecting) {
            HttpImpl.launch15Collect.num += 1;
            const curPath = HttpImpl.launch15Collect.requestPathMap.get(requestPath);
            if (!curPath) {
                HttpImpl.launch15Collect.requestPathMap.set(requestPath, 1);
            }
            else {
                HttpImpl.launch15Collect.requestPathMap.set(requestPath, curPath + 1);
            }
        }
        if (HttpImpl.routeCollect.isCollecting) {
            HttpImpl.routeCollect.num += 1;
            const curPath = HttpImpl.routeCollect.requestPathMap.get(requestPath);
            if (!curPath) {
                HttpImpl.routeCollect.requestPathMap.set(requestPath, 1);
            }
            else {
                HttpImpl.routeCollect.requestPathMap.set(requestPath, curPath + 1);
            }
        }
    }
    genRequestPath(map) {
        const entries = [...map.entries()];
        let count = 0;
        const result = {};
        while (entries.length > 0 && count < 50) {
            const curEntries = entries.splice(0, 5);
            if (curEntries.length > 0) {
                const str = curEntries.reduce((t, c) => {
                    const [key, value] = c;
                    return t + `${key}@@@${value};`;
                }, '');
                result[`requestUrl${count}`] = str;
            }
            count += 1;
        }
        return result;
    }
    startRouteCollect() {
        if (!inWindow()) {
            return;
        }
        if (!this.systemApi.getCurrentUser()) {
            return;
        }
        HttpImpl.routeCollect.isCollecting = true;
        if (!HttpImpl.routeCollect.timer) {
            HttpImpl.routeCollect.timer = window.setTimeout(() => {
                HttpImpl.routeCollect.isCollecting = false;
                this.reportRequestNum('route');
            }, HttpImpl.routeCollect.duration);
        }
    }
    startLaunchCollect() {
        if (!inWindow()) {
            return;
        }
        if (!this.systemApi.getCurrentUser()) {
            return;
        }
        HttpImpl.launchCollect.isCollecting = true;
        const currentUrl = this.getPageModule(window.location.href);
        if (currentUrl) {
            HttpImpl.launchCollect.launchUrlSet.add(currentUrl);
        }
        if (!HttpImpl.launchCollect.timer) {
            HttpImpl.launchCollect.timer = window.setTimeout(() => {
                HttpImpl.launchCollect.isCollecting = false;
                this.reportRequestNum('launch');
            }, HttpImpl.launchCollect.duration);
        }
    }
    startLaunch15Collect() {
        if (!inWindow()) {
            return;
        }
        if (!this.systemApi.getCurrentUser()) {
            return;
        }
        HttpImpl.launch15Collect.isCollecting = true;
        const currentUrl = this.getPageModule(window.location.href);
        if (currentUrl) {
            HttpImpl.launch15Collect.launchUrlSet.add(currentUrl);
        }
        if (!HttpImpl.launch15Collect.timer) {
            HttpImpl.launch15Collect.timer = window.setTimeout(() => {
                HttpImpl.launch15Collect.isCollecting = false;
                this.reportRequestNum('launch15');
            }, HttpImpl.launch15Collect.duration);
        }
    }
    reportRequestNum(type) {
        if (!inWindow()) {
            return;
        }
        if (!this.systemApi.getCurrentUser()) {
            return;
        }
        const reportLaunch = type === 'launch';
        const reportLaunch15 = type === 'launch15';
        const reportRoute = type === 'route';
        if (reportLaunch && !HttpImpl.launchCollect.isReported) {
            HttpImpl.launchCollect.isReported = true;
            console.warn('[request collect] launch', HttpImpl.launchCollect.num);
            this.dataTrackerApi.track('request_num_launch', Object.assign({ num: HttpImpl.launchCollect.num || 0, launchUrls: [...HttpImpl.launchCollect.launchUrlSet].join('@@@') }, this.genRequestPath(HttpImpl.launchCollect.requestPathMap)));
            clearTimeout(HttpImpl.launchCollect.timer);
            HttpImpl.launchCollect.timer = 0;
            HttpImpl.launchCollect.num = 0;
            HttpImpl.launchCollect.launchUrlSet.clear();
            HttpImpl.launchCollect.requestPathMap.clear();
        }
        if (reportLaunch15 && !HttpImpl.launch15Collect.isReported) {
            HttpImpl.launch15Collect.isReported = true;
            console.warn('[request collect] launch15', HttpImpl.launch15Collect.num);
            this.dataTrackerApi.track('request_num_launch_15', Object.assign({ num: HttpImpl.launch15Collect.num || 0, launchUrls: [...HttpImpl.launch15Collect.launchUrlSet].join('@@@') }, this.genRequestPath(HttpImpl.launch15Collect.requestPathMap)));
            clearTimeout(HttpImpl.launch15Collect.timer);
            HttpImpl.launch15Collect.timer = 0;
            HttpImpl.launch15Collect.num = 0;
            HttpImpl.launch15Collect.launchUrlSet.clear();
            HttpImpl.launch15Collect.requestPathMap.clear();
        }
        if (reportRoute) {
            console.warn('[request collect] route', HttpImpl.routeCollect.num);
            this.dataTrackerApi.track('request_num_route', Object.assign({ num: HttpImpl.routeCollect.num || 0, url: HttpImpl.routeCollect.currentUrl }, this.genRequestPath(HttpImpl.routeCollect.requestPathMap)));
            const x = HttpImpl.routeCollect.num;
            const y = this.genRequestPath(HttpImpl.routeCollect.requestPathMap);
            console.warn('[request collect] route num', x);
            console.warn('[request collect] route request path', y);
            clearTimeout(HttpImpl.routeCollect.timer);
            HttpImpl.routeCollect.timer = 0;
            HttpImpl.routeCollect.num = 0;
            HttpImpl.routeCollect.requestPathMap.clear();
        }
    }
    cancelRequestCollect() {
        clearTimeout(HttpImpl.launchCollect.timer);
        clearTimeout(HttpImpl.launch15Collect.timer);
        clearTimeout(HttpImpl.routeCollect.timer);
        HttpImpl.launchCollect = {
            num: 0,
            timer: 0,
            isCollecting: false,
            isReported: false,
            duration: 5000,
            launchUrlSet: new Set(),
            requestPathMap: new Map(),
        };
        HttpImpl.launch15Collect = {
            num: 0,
            timer: 0,
            isCollecting: false,
            isReported: false,
            duration: 15000,
            launchUrlSet: new Set(),
            requestPathMap: new Map(),
        };
        HttpImpl.routeCollect = {
            num: 0,
            timer: 0,
            isCollecting: false,
            duration: 5000,
            currentUrl: '',
            requestPathMap: new Map(),
        };
    }
    init() {
        if (!HttpImpl.isInited) {
            this.eventApi.registerSysEventObserver('loginExpiredCrossWindow', {
                name: 'HttpWatchLoginExpiredCross',
                func: this.watchLoginExpired.bind(this),
            });
            this.eventApi.registerSysEventObserver('subAccountLoginExpiredCrossWindow', {
                name: 'httpImpl-subAccountLoginExpiredCrossWindow',
                func: this.watchSubAccountLoginExpired.bind(this),
            });
            this.eventApi.registerSysEventObserver('subAccountLogin', {
                name: 'httpImpl-subAccountLogin',
                func: this.handleSubAccountLogin.bind(this),
            });
            this.eventApi.registerSysEventObserver('SubAccountLoginExpired', {
                name: 'httpImpl-SubAccountLoginExpired',
                func: this.handleSubAccountLoginExpired.bind(this),
            });
            this.eventApi.registerSysEventObserver('autoLogin', {
                name: 'httpImpl-autoLogin',
                func: this.handleAutoLogin.bind(this),
            });
        }
        HttpImpl.isInited = true;
        return this.name;
    }
    handleSubAccountLoginExpired(ev) {
        if (ev && ev.eventData && ev.eventData.subAccount) {
            const { subAccount } = ev.eventData;
            this.resetSubAccountState(subAccount);
            HttpImpl.reLoginSubAccountRequest[subAccount] = [];
        }
    }
    isAxiosResponse(e) {
        return typeof (e === null || e === void 0 ? void 0 : e.status) === 'number' && (e === null || e === void 0 ? void 0 : e.data);
    }
    updateDeviceInfo() {
        return this.systemApi.getDeviceInfo().then(res => {
            if (!this.deviceConfig) {
                this.deviceConfig = res;
            }
            else {
                if (res && res._deviceId !== this.deviceConfig._deviceId) {
                    this.deviceConfig._deviceId = res._deviceId;
                }
            }
        });
    }
    afterInit() {
        this.updateDeviceInfo();
        if (this.systemApi.getCurrentUser()) {
            this.httpCacheDb.init();
            this.startLaunchCollect();
            this.startLaunch15Collect();
        }
        else {
        }
        return this.name;
    }
    getLogoutPage() {
        return HttpImpl.logoutPage;
    }
    afterLoadFinish() {
        this.buildLogoutPage();
        return this.name;
    }
    buildLogoutPage() {
        if (this.systemApi.getCurrentUser()) {
            const currentAccount = this.systemApi.getCurrentUser();
            HttpImpl.logoutPage = this.buildUrl(HttpImpl.loginPageBase, {
                from: locationHelper.getHost(),
                uid: currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.id,
                domain: currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.domain,
            });
        }
    }
    judgeUrlNeedAuth(input) {
        const url = createNewUrl(input);
        let pathName = url.pathname;
        if (pathName.startsWith('/lx-web/')) {
            pathName = pathName.slice(7);
        }
        const noAuth = pathName.startsWith('/domain/') ||
            pathName.startsWith('/bjdomain/') ||
            pathName.startsWith('/lxdomain/') ||
            pathName.startsWith('/bjlxdomain/') ||
            pathName.startsWith('/login/') ||
            pathName.startsWith('/entry/') ||
            pathName.startsWith('/bjentry/') ||
            pathName.startsWith('/commonweb/') ||
            pathName.startsWith('/bjcommonweb/') ||
            pathName.startsWith('/config/api/') ||
            pathName.startsWith('/service/register') ||
            pathName.indexOf('/edisk/api/biz/external') >= 0 ||
            pathName.indexOf('/corp-mail/auth') >= 0 ||
            pathName.indexOf('/cowork/api/biz/enter/accountInfo') >= 0 ||
            pathName.indexOf('/cowork/api/biz/enter/emailContactList') >= 0 ||
            pathName.indexOf('/corp-mail/bind_hosts/auth/preLogin') >= 0 ||
            pathName.indexOf('/privilege/api/biz/privilege/sirius/getAll') >= 0 ||
            pathName.indexOf('/privilege/api/biz/product/version/feature/privilege/getAll') >= 0 ||
            pathName.indexOf('/miniapp/qrcode/check') >= 0 ||
            pathName.indexOf('/customer/api/biz/config/notice') >= 0 ||
            pathName.indexOf('/commonweb/account/getAccountBaseInfo') >= 0 ||
            pathName.indexOf('/sirius/it-others/api/biz/sirius/login/code') >= 0 ||
            pathName.indexOf('/privilege/api/biz/product/menu/account/menuVersion') >= 0 ||
            pathName.indexOf('/it-others/api/biz/config/notice') >= 0 ||
            pathName.indexOf('/sirius/auth-center/api/pub/auth/check-auth-info') >= 0 ||
            pathName.indexOf('/sirius/pub-feedback/') >= 0 ||
            pathName.indexOf('/cowork-storage/clieng-log/') >= 0;
        return !noAuth;
    }
}
HttpImpl.noRelogin = debugging && !isProd;
HttpImpl.profile = confFunc('profile');
HttpImpl.loginPageBase = HttpImpl.profile && HttpImpl.profile.startsWith('webmail') ? confFunc('logoutPage') : confFunc('loginPage');
HttpImpl.logoutPage = HttpImpl.loginPageBase;
HttpImpl.MAX_CACHE_REQ_TIME = 1500;
HttpImpl.MAX_RE_LOGIN_EXPIRED_TIME = 2 * 60 * 1000;
HttpImpl.MAX_RETRY_TIMES = 25;
HttpImpl.EACH_REQ_MAX_RETRY_TIMES = 3;
HttpImpl.MAX_CACHE_FAILED_TIMES = 40;
HttpImpl.host = confFunc('host');
HttpImpl.comNotExist = confFunc('notExistUrl');
HttpImpl.cacheFailedTime = 0;
HttpImpl.cacheSubAccountFailedTime = {};
HttpImpl.focused = true;
HttpImpl.reLoginTimeMap = {};
HttpImpl.reLoginSubAccountTimeMap = {};
HttpImpl.urlConfigMap = {};
HttpImpl.UrlConfigs = [];
HttpImpl.processingReqs = {};
HttpImpl.processingSubAccountReqs = {};
HttpImpl.seq = new SequenceHelper();
HttpImpl.requestFailedEntry = new Set();
HttpImpl.reLoginProcessing = false;
HttpImpl.reLoginSubaccountProcessing = {};
HttpImpl.reLoginRequest = [];
HttpImpl.reLoginSubAccountRequest = {};
HttpImpl.lastSubAccountReLoginTime = {};
HttpImpl.isInited = false;
HttpImpl.launchCollect = {
    num: 0,
    timer: 0,
    isCollecting: false,
    isReported: false,
    duration: 5000,
    launchUrlSet: new Set(),
    requestPathMap: new Map(),
};
HttpImpl.launch15Collect = {
    num: 0,
    timer: 0,
    isCollecting: false,
    isReported: false,
    duration: 15000,
    launchUrlSet: new Set(),
    requestPathMap: new Map(),
};
HttpImpl.routeCollect = {
    num: 0,
    timer: 0,
    isCollecting: false,
    duration: 5000,
    currentUrl: '',
    requestPathMap: new Map(),
};
const httpImpl = new HttpImpl();
api.registerDataTransApi(httpImpl);
export default httpImpl;
